import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpRequest, HttpResponse, HttpClient } from '@angular/common/http';
import { Observable, Subject, throwError, BehaviorSubject, race } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { OpenReplayService } from './open-replay.service'; // Import TrackerService
import { environment } from 'src/environments/environment';

/** Prevents infinite refresh → retry → 401 loops when auth fails for reasons other than an expired JWT. */
const AUTH_REFRESH_RETRIED_HEADER = 'X-Auth-Refresh-Retried';

@Injectable({
  providedIn: 'root'
})
export class AuthInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  /** Releases 401 waiters when refresh fails. next(null) alone leaves them parked forever. */
  private readonly refreshError$ = new Subject<unknown>();

  private readonly diag =
    (environment as { authDiagnosticLogging?: boolean }).authDiagnosticLogging === true;

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly openReplayService: OpenReplayService // Inject TrackerService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isRefreshUrl = req.url.endsWith('/auth/refresh');

    let authRequest = req;
    if (this.shouldAttachAccessToken(req)) {
      const authToken = this.authService.getToken();

      if (authToken) {
        authRequest = this.addTokenHeader(req, authToken);
      }
    }

    return next.handle(authRequest).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          if (isRefreshUrl) {
            return throwError(() => error);
          }
          if (authRequest.headers.has(AUTH_REFRESH_RETRIED_HEADER)) {
            return throwError(() => error);
          }
          const message = error.error?.faults?.[0]?.message || '';
          if (message.toLowerCase() === 'incorrect password' || message.toLowerCase() === 'not yet approved') {
            return throwError(() => error);
          }
          if (this.diag) {
            console.debug('[AuthInterceptor] HTTP 401; attempting token refresh', {
              url: authRequest.url,
              method: authRequest.method,
            });
          }
          return this.handle401Error(authRequest, next);
        }

        return throwError(() => error);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.authService.getRefreshToken();

      if (refreshToken) {
        return this.authService.refreshToken().pipe(
          switchMap((res: any) => {
            this.refreshTokenSubject.next(res.token);
            this.isRefreshing = false;
            if (this.diag) {
              console.debug('[AuthInterceptor] Refresh succeeded; retrying original request', {
                url: request.url,
              });
            }
            return next.handle(this.retriedRequest(request, res.token));
          }),
          catchError((err) => {
            const usable = this.authService.hasUsableAccessToken() ? this.authService.getToken() : '';
            if (usable) {
              // A parallel refresh already rotated and installed a live access JWT.
              // Logging out here would revoke that new refresh token.
              this.refreshTokenSubject.next(usable);
              this.isRefreshing = false;
              if (this.diag) {
                console.debug('[AuthInterceptor] Refresh rejected after a newer access token was issued; keeping session', {
                  url: request.url,
                });
              }
              return next.handle(this.retriedRequest(request, usable));
            }

            this.refreshError$.next(err);
            this.refreshTokenSubject.next(null);
            this.isRefreshing = false;
            if (this.diag) {
              console.debug('[AuthInterceptor] Refresh failed; logging out', err);
            }
            this.authService.logout({ email: this.authService.getUser()?.email || '' }).subscribe(() => {
              // Do not send users away from public deep links (e.g. `/discover/307`, `/dictionary/proof-of-work` after a follow-up API 401).
              if (this.shouldStayOnCurrentUrlAfterLogout()) {
                return;
              }
              this.router.navigate(['/home']);
            });
            return throwError(() => err);
          })
        );
      } else {
        // No refresh token: user is not logged in. Do not redirect to /login so public
        // pages (e.g. `/home`, `/discover/:id`, `/dictionary/:slug`) can load; let the request fail and the page handle it.
        this.refreshError$.next(new HttpErrorResponse({
          error: null,
          status: 401,
          statusText: 'Unauthorized'
        }));
        this.isRefreshing = false;
        return throwError(() => new HttpErrorResponse({
          error: null,
          status: 401,
          statusText: 'Unauthorized'
        }));
      }
    }

    return this.waitForRefresh(request, next);
  }

  /** 401s that arrived while a refresh was in flight retry once it settles, or fail if it fails. */
  private waitForRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return race(
      this.refreshTokenSubject.pipe(
        filter((token): token is string => typeof token === 'string' && token.length > 0),
        take(1),
        map((token) => ({ kind: 'token' as const, token })),
      ),
      this.refreshError$.pipe(
        take(1),
        map((error) => ({ kind: 'error' as const, error })),
      ),
    ).pipe(
      switchMap((result) => {
        if (result.kind === 'error') {
          return throwError(() => result.error);
        }
        return next.handle(this.retriedRequest(request, result.token));
      }),
    );
  }

  private retriedRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return this.addTokenHeader(request, token).clone({
      setHeaders: { [AUTH_REFRESH_RETRIED_HEADER]: 'true' },
    });
  }

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  /**
   * Anonymous auth endpoints must not receive a stale/expired access JWT — the API still
   * runs JwtBearer validation when Authorization is present, which breaks /auth/refresh
   * and can interfere with re-login while old tokens remain in storage.
   */
  private shouldAttachAccessToken(req: HttpRequest<unknown>): boolean {
    if (req.url.includes('sig=')) {
      return false;
    }

    const anonymousAuthPaths = [
      '/auth/refresh',
      '/auth/login',
      '/auth/logout',
      '/auth/register',
      '/auth/registerex',
      '/auth/registerorganisation',
      '/auth/user-exists-email',
      '/auth/user-exists-google',
      '/auth/check-organisation-by-email',
      '/auth/request-validation-code-email',
      '/auth/validate-registration-code',
      '/auth/send-registration-received-email',
      '/auth/confirm-email',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/auth/microsoft-login',
      '/auth/google-login',
    ];

    return !anonymousAuthPaths.some((segment) => req.url.includes(segment));
  }

  /** After session clear, keep the user on these public URLs instead of sending them to `/home`. */
  private shouldStayOnCurrentUrlAfterLogout(): boolean {
    let path = (this.router.url.split('?')[0].split('#')[0] || '/').trim();
    path = path.replace(/\/$/, '') || '/';
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }

    // Public learning readers (same idea as article detail + sponsor / follow-up calls after load).
    if (path === '/discover' || /^\/discover\/\d+$/.test(path)) {
      return true;
    }
    if (path === '/dictionary' || path.startsWith('/dictionary/')) {
      return true;
    }
    if (
      path === '/' ||
      path === '/home' ||
      path === '/discover' ||
      path === '/marketplace' ||
      path === '/directory' ||
      path === '/experts' ||
      path.startsWith('/experts/') ||
      path === '/community'
    ) {
      return true;
    }
    if (path.startsWith('/referral/') || path.startsWith('/shop/') || path === '/mcp/consent') {
      return true;
    }
    if (/^\/organisation\/[^/]+$/.test(path)) {
      return true;
    }
    return false;
  }
}
